import { create } from 'zustand';
import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import {
  authEnabled,
  currentSession,
  signIn as cognitoSignIn,
  completeNewPassword as cognitoCompleteNewPassword,
  forgotPassword as cognitoForgotPassword,
  confirmForgotPassword as cognitoConfirmForgotPassword,
  signOut as cognitoSignOut,
  accessToken as readAccessToken,
} from './cognito';

type Status =
  | 'loading'           // restoring a persisted session at boot
  | 'unauthenticated'   // show the login form
  | 'newPassword'       // first-login NEW_PASSWORD_REQUIRED challenge
  | 'forgotRequest'     // collecting email to send a reset code
  | 'forgotConfirm'     // collecting the emailed code + new password
  | 'authenticated'     // app is mounted
  | 'disabled';         // Cognito not configured (local dev) — bypass the gate

interface AuthState {
  status: Status;
  email: string | null;
  accessToken: string | null;
  error: string | null;
  busy: boolean;
  /** Carried between the NEW_PASSWORD_REQUIRED challenge and its completion. */
  pendingUser: CognitoUser | null;

  /** Last email entered — carried into the reset flow so confirm knows the user. */
  notice: string | null;

  bootstrap: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  startForgot: () => void;
  backToSignIn: () => void;
  requestReset: (email: string) => Promise<void>;
  confirmReset: (code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: authEnabled() ? 'loading' : 'disabled',
  email: null,
  accessToken: null,
  error: null,
  busy: false,
  pendingUser: null,
  notice: null,

  bootstrap: async () => {
    if (!authEnabled()) { set({ status: 'disabled' }); return; }
    const restored = await currentSession();
    if (restored) {
      set({ status: 'authenticated', email: restored.email, accessToken: readAccessToken(restored.session) });
    } else {
      set({ status: 'unauthenticated' });
    }
  },

  signIn: async (email, password) => {
    set({ busy: true, error: null });
    try {
      const r = await cognitoSignIn(email, password);
      if (r.kind === 'newPassword') {
        set({ status: 'newPassword', pendingUser: r.user, email: r.email, busy: false });
      } else {
        set({ status: 'authenticated', email: r.email, accessToken: readAccessToken(r.session), busy: false });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'sign-in failed', busy: false });
    }
  },

  completeNewPassword: async (newPassword) => {
    const user = get().pendingUser;
    if (!user) { set({ error: 'no pending challenge', status: 'unauthenticated' }); return; }
    set({ busy: true, error: null });
    try {
      const session: CognitoUserSession = await cognitoCompleteNewPassword(user, newPassword);
      set({ status: 'authenticated', accessToken: readAccessToken(session), pendingUser: null, busy: false });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'could not set password', busy: false });
    }
  },

  startForgot: () => set({ status: 'forgotRequest', error: null, notice: null }),

  backToSignIn: () => set({ status: 'unauthenticated', error: null, notice: null }),

  requestReset: async (email) => {
    set({ busy: true, error: null });
    try {
      await cognitoForgotPassword(email);
      // Carry the email forward; the confirm step needs the same username.
      set({ status: 'forgotConfirm', email, busy: false });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'could not start reset', busy: false });
    }
  },

  confirmReset: async (code, newPassword) => {
    const email = get().email;
    if (!email) { set({ error: 'missing email', status: 'forgotRequest' }); return; }
    set({ busy: true, error: null });
    try {
      await cognitoConfirmForgotPassword(email, code, newPassword);
      // Don't auto-sign-in (confirmPassword yields no session); send them back
      // to the form with a success notice to log in with the new password.
      set({ status: 'unauthenticated', busy: false, notice: 'Password reset — sign in with your new password.' });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'could not reset password', busy: false });
    }
  },

  signOut: () => {
    cognitoSignOut();
    set({ status: 'unauthenticated', email: null, accessToken: null, error: null, pendingUser: null, notice: null });
  },
}));

/** Synchronous access-token read for the WS URL builder (config.ts). */
export function currentAccessToken(): string | null {
  return useAuth.getState().accessToken;
}
